import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { motion } from "framer-motion";
import { Steps } from './Steps';
import { useGame } from '../../contexts/GameContext';
import { StepContent } from './StepContent';
import { NavigationControls } from './NavigationControls';
import { tutorialSteps } from './tutorialSteps';
import { Button } from '@/components/ui/button';
import { MessageSquare, X } from 'lucide-react';

interface OnboardingTutorialProps {
  onComplete: () => void;
}

const OnboardingTutorial: React.FC<OnboardingTutorialProps> = ({ onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const { setHasTutorialCompleted } = useGame();
  const [showTips, setShowTips] = useState(true);
  
  const handleNext = () => {
    if (currentStep < tutorialSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      setHasTutorialCompleted(true);
      onComplete();
    }
  };

  const handleBack = () => {
    currentStep > 0 && setCurrentStep(currentStep - 1);
  };
  
  const handleSkip = () => {
    // Confirm the user wants to skip
    if (window.confirm("Are you sure you want to skip the tutorial? You can access it later from the help menu.")) {
      setHasTutorialCompleted(true);
      onComplete();
    }
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        when: "beforeChildren",
        staggerChildren: 0.3
      }
    }
  };

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      className="relative"
    >
      <Card className="w-full max-w-4xl mx-auto bg-web3-blue/20 border border-web3-blue/20">
        <CardHeader className="text-center">
          {/* Action buttons positioned absolutely */}
          <div className="absolute top-4 right-4 flex space-x-2">
            <Button 
              variant="ghost" 
              size="sm"
              className="text-white/70 hover:text-white"
              onClick={() => setShowTips(!showTips)}
            >
              <MessageSquare className="h-4 w-4 mr-1" />
              {showTips ? 'Hide Tips' : 'Show Tips'}
            </Button>
            
            <Button 
              variant="ghost" 
              size="icon"
              className="text-white/70 hover:text-white"
              onClick={handleSkip}
              title="Skip Tutorial"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          
          <CardTitle className="text-3xl text-web3-green">Career Simulator Tutorial</CardTitle>
          <CardDescription className="text-lg">
            Learn how the simulation works in {tutorialSteps.length} easy steps
          </CardDescription>
          
          {/* Navigation buttons below title */}
          <NavigationControls 
            currentStep={currentStep}
            totalSteps={tutorialSteps.length}
            onBack={handleBack}
            onNext={handleNext}
          />
        </CardHeader>
        <CardContent>
          <Steps steps={tutorialSteps.length} currentStep={currentStep} />
          
          {/* Step content container with animations */}
          <StepContent 
            step={tutorialSteps[currentStep]} 
            stepIndex={currentStep} 
          />
          
          {/* Contextual tips that can be toggled */}
          {showTips && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-6 p-3 border border-web3-green/30 bg-web3-green/10 rounded-md"
            >
              <h4 className="flex items-center text-web3-green text-sm font-medium">
                <MessageSquare className="h-4 w-4 mr-2" />
                Pro Tip
              </h4>
              <p className="text-xs text-white/80 mt-1">
                {getTipForStep(currentStep)}
              </p>
            </motion.div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
};

// Helper function to get contextual tips based on the current step
function getTipForStep(stepIndex: number): string {
  switch(stepIndex) {
    case 0:
      return "The simulation is designed to be educational while still being enjoyable. Don't worry about making 'perfect' decisions - focus on learning about the Bitcoin mining industry.";
    case 1:
      return "Choose a role that aligns with your interests rather than what seems easiest. Each role provides a unique perspective on the Bitcoin mining industry.";
    case 2:
      return "When facing challenges, consider the long-term impact on your career, not just immediate benefits. Some decisions have delayed effects that become apparent later.";
    case 3:
      return "Skills that synergize with each other create powerful combinations. Look for skills that complement your role's strengths or shore up weaknesses.";
    case 4:
      return "Time management is crucial. During rapid industry changes, you might want to advance time quickly; during stable periods, take your time to make strategic decisions.";
    case 5:
      return "At each career level, focus on meeting the specific objectives for that stage rather than trying to optimize everything at once.";
    case 6:
      return "If you're new to the simulation, start with Normal difficulty. You can always increase the challenge later as you become more familiar with the mechanics.";
    case 7:
      return "The journey matters more than the destination. Even if you don't reach 'Industry Expert' status, you'll gain valuable insights about career progression in Bitcoin mining.";
    default:
      return "Take your time to explore all aspects of the simulation. There's no rush to complete it, and different paths offer unique experiences.";
  }
}

export default OnboardingTutorial;
